import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Eye, Plus, Save, Upload, X, FileText } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { DatePicker } from '../ui/DatePicker';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Select';
import { Tooltip } from '../ui/Tooltip';
import { trainingProducts } from '../../data/trainingProducts';
import { useWorkerTraining } from '../../hooks/useWorkerTraining';
import { workerTrainingStore } from '../../services/workerTrainingStore';
import { WorkerCourse } from '../../types/worker';
import { certificateFileName, courseExpiryDate, courseSourceLabels, formatItDate, isCourseExpired, validityLabel } from '../../utils/workers';
interface WorkerTrainingTabProps {
  // Saved worker: records are read from and written to the training database
  workerId?: number;
  // New worker (not saved yet): local records only
  initialCourses?: WorkerCourse[];
  workerName?: string;
}
function emptyCourse(): WorkerCourse {
  return {
    id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    firstDate: '',
    updateDate: '',
    expiryDate: '',
    certificateName: '',
    expiryAlert: true,
    source: 'diretta'
  };
}
export function WorkerTrainingTab({
  workerId,
  initialCourses = [],
  workerName
}: WorkerTrainingTabProps) {
  const stored = useWorkerTraining(workerId);
  const [local, setLocal] = useState<WorkerCourse[]>(initialCourses);
  const [drafts, setDrafts] = useState<WorkerCourse[]>([]);
  const [previewCourse, setPreviewCourse] = useState<WorkerCourse | null>(null);
  const isStored = workerId !== undefined;
  const courses = isStored ? stored : local;
  const updateCourse = (id: string, changes: Partial<WorkerCourse>) => {
    if (workerId !== undefined) workerTrainingStore.update(workerId, id, changes);else setLocal(prev => prev.map(c => c.id === id ? {
      ...c,
      ...changes
    } : c));
  };
  const updateDraft = (id: string, changes: Partial<WorkerCourse>) => setDrafts(prev => prev.map(c => {
    if (c.id !== id) return c;
    const next = {
      ...c,
      ...changes
    };
    // Expiry calculated from the validity years of the course
    if ('productCode' in changes || 'firstDate' in changes || 'updateDate' in changes) {
      const expiry = courseExpiryDate(next.productCode, next.updateDate || next.firstDate);
      if (expiry) next.expiryDate = expiry;
    }
    return next;
  }));
  const saveDraft = (draft: WorkerCourse) => {
    if (workerId !== undefined) workerTrainingStore.add(workerId, draft);else setLocal(prev => [...prev, draft]);
    setDrafts(prev => prev.filter(d => d.id !== draft.id));
  };
  const renderStatus = (course: WorkerCourse) => {
    if (course.done === false) return <Badge variant="warning" size="sm" className="whitespace-nowrap" data-id="element-2260" data-mp="1o6pwl9txbprq">
          Da svolgere
        </Badge>;
    const expired = isCourseExpired(course);
    return <Badge variant={expired ? 'danger' : 'success'} size="sm" data-id="element-2261" data-mp="1ll8j0lp6nxc5">
        {expired ? 'Scaduto' : 'Valido'}
      </Badge>;
  };
  const renderSource = (course: WorkerCourse) => <div className="whitespace-nowrap" data-id="element-2262" data-mp="1buw00xx0oc84">
      <p className="text-xs font-medium text-slate-700" data-id="element-2263" data-mp="1brrqdl0s8oa2">
        {courseSourceLabels[course.source || 'diretta']}
      </p>
      {course.source === 'indiretta' && course.practiceId && <Link to={`/training/pratiche/${course.practiceId}/modifica`} className="text-xs font-mono text-seform-orange hover:underline" data-id="element-2264" data-mp="24gp2f6eehoud">
          {course.courseCode || 'Pratica'}
        </Link>}
    </div>;
  const renderAlert = (course: WorkerCourse, onToggle: (checked: boolean) => void) => <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap" data-id="element-2265" data-mp="j5ygkrh2k9ds">
      <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={course.expiryAlert} onChange={e => onToggle(e.target.checked)} aria-label={`Avviso scadenza ${course.name || 'nuovo corso'}`} data-id="element-2266" data-mp="f3v542ueh4si" />
      <span className="text-xs text-slate-600" data-id="element-2267" data-mp="1t2dflzl3m6ig">Avviso scadenza</span>
    </label>;
  const renderCertificate = (course: WorkerCourse, onUpload: () => void) => course.done === false ? <span className="text-xs text-slate-400" data-id="element-2268" data-mp="1xz3syf5iqo06">—</span> : course.certificateName ? <Button type="button" variant="ghost" size="sm" className="!px-2 text-seform-orange whitespace-nowrap" onClick={() => setPreviewCourse(course)} startContent={<Eye className="h-4 w-4" data-id="element-2270" data-mp="2csp6di1v1dya" />} data-id="element-2269" data-mp="2loy50ui0k8jd">
        Vedi attestato
      </Button> : <Button type="button" variant="flat" color="secondary" size="sm" className="!px-2 whitespace-nowrap" onClick={onUpload} startContent={<Upload className="h-3.5 w-3.5" data-id="element-2272" data-mp="15e3yhayty6xn" />} data-id="element-2271" data-mp="2gu109zrrolca">
        Upload attestato
      </Button>;
  return <div className="space-y-4" data-id="element-2273" data-mp="xvcub3uyrwsn">
      <div className="flex justify-between items-center sticky top-0 bg-white z-10 py-1 pr-1" data-id="element-2274" data-mp="sufc4r9yf2r5">
        <h4 className="font-bold text-seform-navy" data-id="element-2275" data-mp="17r63qeddza67">Corsi effettuati</h4>
        <Button type="button" size="sm" startContent={<Plus className="h-4 w-4" data-id="element-2277" data-mp="12sw7e0fxe3j0" />} onClick={() => setDrafts(prev => [...prev, emptyCourse()])} data-id="element-2276" data-mp="17pd5nngu9y9t">
          Aggiungi corso
        </Button>
      </div>

      <div className="border border-slate-100 rounded-xl overflow-x-auto" data-id="element-2278" data-mp="a4gvhms3c1bw">
        <table className="w-full text-sm text-left" data-id="element-2279" data-mp="osbol4a0g7pi">
          <thead className="bg-slate-50 text-slate-500 font-semibold" data-id="element-2280" data-mp="ou4v8jkmirxi">
            <tr data-id="element-2281" data-mp="k0tyxcdfttju">
              <th className="p-3 min-w-[180px]" data-id="element-2282" data-mp="k13gqcobwueg">Corso</th>
              <th className="p-3 whitespace-nowrap" data-id="element-2283" data-mp="3shewzy4j7286">Data primo svolgimento</th>
              <th className="p-3 whitespace-nowrap" data-id="element-2284" data-mp="3sfc33309coy6">Data aggiornamento</th>
              <th className="p-3 whitespace-nowrap" data-id="element-2285" data-mp="3nibv2mjulc4f">Data scadenza</th>
              <th className="p-3" data-id="element-2286" data-mp="h0abs6evgwjr">Attestato</th>
              <th className="p-3 whitespace-nowrap" data-id="element-2287" data-mp="gzinzhrio341">Avviso scadenza</th>
              <th className="p-3" data-id="element-2288" data-mp="l1dpondd0iic">Stato</th>
              <th className="p-3" data-id="element-2289" data-mp="ptx39kc1rdn5">Origine</th>
              <th className="p-3" data-id="element-2290" data-mp="puyh1wkg6e8k">
                <span className="sr-only" data-id="element-2291" data-mp="pvzsi25dl2qt">Azioni</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-2292" data-mp="3u7v8c1uqgvcp">
            {courses.length === 0 && drafts.length === 0 && <tr data-id="element-2293" data-mp="23j8vn5zp2pe">
                <td colSpan={9} className="p-6 text-center text-slate-400" data-id="element-2294" data-mp="2541v8cnodl5">
                  Nessun corso registrato. Usa "Aggiungi corso" per inserirne uno.
                </td>
              </tr>}
            {courses.map(course => <tr key={course.id} data-id="element-2295" data-mp="bzw3cn9czkl7">
                <td className="p-3 font-medium" data-id="element-2296" data-mp="1ij3z2bk49p4p">{course.name}</td>
                <td className="p-3 whitespace-nowrap" data-id="element-2297" data-mp="1nhfa2pm5e5oi">
                  {course.done === false ? <span className="text-slate-400" data-id="element-2298" data-mp="37zga9el0mou0">
                      {course.firstDate ? `Previsto ${formatItDate(course.firstDate)}` : '-'}
                    </span> : formatItDate(course.firstDate)}
                </td>
                <td className="p-3 whitespace-nowrap" data-id="element-2299" data-mp="2y7ab9kwcj75b">{formatItDate(course.updateDate)}</td>
                <td className="p-3 whitespace-nowrap" data-id="element-2300" data-mp="2t8pl6eat63ul">{formatItDate(course.expiryDate)}</td>
                <td className="p-3" data-id="element-2301" data-mp="2p6vlo9ccn5w0">
                  {renderCertificate(course, () => updateCourse(course.id, {
                certificateName: certificateFileName(course.name)
              }))}
                </td>
                <td className="p-3" data-id="element-2302" data-mp="2abxuvkwumoh1">
                  {renderAlert(course, checked => updateCourse(course.id, {
                expiryAlert: checked
              }))}
                </td>
                <td className="p-3" data-id="element-2303" data-mp="dimis8i3zu9n">{renderStatus(course)}</td>
                <td className="p-3" data-id="element-2304" data-mp="8le1090vhs8x">{renderSource(course)}</td>
                <td className="p-3" data-id="element-2305" data-mp="3dkcwaa2kdtc9"></td>
              </tr>)}
            {drafts.map(draft => <tr key={draft.id} className="bg-orange-50/30 align-top" data-id="element-2306" data-mp="3ij69e1dxvibw">
                <td className="p-2" data-id="element-2307" data-mp="3ilaeunrhkpea">
                  <Select aria-label="Corso" value={draft.productCode || ''} onChange={e => {
                const product = trainingProducts.find(p => p.code === e.target.value);
                updateDraft(draft.id, {
                  productCode: product?.code,
                  name: product?.name || ''
                });
              }} options={[{
                value: '',
                label: 'Seleziona corso...'
              }, ...trainingProducts.map(p => ({
                value: p.code,
                label: p.name
              }))]} data-id="element-2308" data-mp="34suig5t3bpmc" />
                  {draft.productCode && <p className="text-xs text-slate-500 mt-1" data-id="element-2309" data-mp="o247rb6anwk1">{validityLabel(draft.productCode)}</p>}
                </td>
                <td className="p-2" data-id="element-2310" data-mp="28j3k0v31ryqe">
                  <DatePicker aria-label="Data primo svolgimento" value={draft.firstDate} onChange={e => updateDraft(draft.id, {
                firstDate: e.target.value
              })} data-id="element-2311" data-mp="24knr6akabw52" />
                </td>
                <td className="p-2" data-id="element-2312" data-mp="2dipwv3eyrcyo">
                  <DatePicker aria-label="Data aggiornamento" value={draft.updateDate} onChange={e => updateDraft(draft.id, {
                updateDate: e.target.value
              })} data-id="element-2313" data-mp="2s8nqvzzkqwvs" />
                </td>
                <td className="p-2" data-id="element-2314" data-mp="317rar66kfgbc">
                  <DatePicker aria-label="Data scadenza" value={draft.expiryDate} onChange={e => updateDraft(draft.id, {
                expiryDate: e.target.value
              })} data-id="element-2315" data-mp="2wasikt6gpjfs" />
                </td>
                <td className="p-2 pt-3" data-id="element-2316" data-mp="11z3y6bhgooo0">
                  {renderCertificate(draft, () => updateDraft(draft.id, {
                certificateName: certificateFileName(draft.name)
              }))}
                </td>
                <td className="p-2 pt-4" data-id="element-2317" data-mp="2ikptpr740ox5">
                  {renderAlert(draft, checked => updateDraft(draft.id, {
                expiryAlert: checked
              }))}
                </td>
                <td className="p-2 pt-4" data-id="element-2318" data-mp="2do7uf6ujtis7">{renderStatus(draft)}</td>
                <td className="p-2 pt-4" data-id="element-2319" data-mp="3ab42xa46hd2h">{renderSource(draft)}</td>
                <td className="p-2 pt-3" data-id="element-2320" data-mp="36aae8eiwvu8j">
                  <div className="flex gap-1" data-id="element-2321" data-mp="36bc0zg9ai563">
                    <Tooltip content="Salva corso" data-id="element-2322" data-mp="31ju2cyuqogoa">
                      <Button type="button" size="sm" variant="flat" color="success" className="!px-2" onClick={() => saveDraft(draft)} disabled={!draft.name} aria-label="Salva corso" data-id="element-2323" data-mp="31iiuamu9gmrd">
                        <Save className="h-3.5 w-3.5" data-id="element-2324" data-mp="h9jcztsu53z" />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Rimuovi" data-id="element-2325" data-mp="5gm7dnwepi85">
                      <Button type="button" size="sm" variant="flat" color="danger" className="!px-2" onClick={() => setDrafts(prev => prev.filter(d => d.id !== draft.id))} aria-label="Rimuovi corso" data-id="element-2326" data-mp="adcu2u2pvc2j">
                        <X className="h-3.5 w-3.5" data-id="element-2327" data-mp="t3ubi2i3ml8l" />
                      </Button>
                    </Tooltip>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Simulated certificate viewer */}
      <Modal isOpen={Boolean(previewCourse)} onClose={() => setPreviewCourse(null)} title="Attestato" size="md" data-id="element-2328" data-mp="173vd5k66twp4">
        {previewCourse && <div className="space-y-6" data-id="element-2329" data-mp="334nl2i7w5ujz">
            <div className="border border-slate-200 rounded-xl p-8 text-center bg-slate-50" data-id="element-2330" data-mp="2z67r1ude7lx2">
              <FileText className="h-10 w-10 text-seform-orange mx-auto mb-3" data-id="element-2331" data-mp="3cxcruzshyk8z" />
              <p className="text-lg font-bold text-seform-navy" data-id="element-2332" data-mp="38634k5uf7t09">{previewCourse.name}</p>
              {workerName && <p className="text-sm text-slate-600 mt-1" data-id="element-2333" data-mp="3muqyk7ombuo3">{workerName}</p>}
              <p className="text-xs text-slate-500 mt-3" data-id="element-2334" data-mp="3mx3nj4ssn5an">
                Svolto il {formatItDate(previewCourse.updateDate || previewCourse.firstDate)} ·
                Scadenza {formatItDate(previewCourse.expiryDate)}
              </p>
              <p className="font-mono text-xs text-slate-400 mt-4" data-id="element-2335" data-mp="3qw2usw1zze59">{previewCourse.certificateName}</p>
            </div>
            <div className="flex justify-end gap-3" data-id="element-2336" data-mp="21ipiajjqejt2">
              <Button type="button" variant="flat" onClick={() => setPreviewCourse(null)} data-id="element-2337" data-mp="1wil1stitpp2u">
                Chiudi
              </Button>
              <Button type="button" startContent={<Download className="h-4 w-4" data-id="element-2339" data-mp="gzlzsb1lx6j9" />} onClick={() => setPreviewCourse(null)} data-id="element-2338" data-mp="c1k851q47yw6">
                Scarica
              </Button>
            </div>
          </div>}
      </Modal>
    </div>;
}